import React, { useCallback, useEffect, useState } from "react";
import { X, Users, Copy, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/invite`;
const mono = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };
const serif = { fontFamily: "'Instrument Serif', 'Times New Roman', serif", fontWeight: 400 };

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never";

export const OwnerInvitesButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="owner-invites-btn"
    className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-stone-700 text-stone-300 text-[11px] font-bold uppercase tracking-[0.18em] hover:border-[#84cc16] hover:text-[#d9f99d] transition-colors"
    style={mono}
  >
    <Users size={15} /> Invites
  </button>
);

const CodeRow = ({ c, onToggle }) => (
  <li
    data-testid={`invite-row-${c.code}`}
    className={`flex items-center gap-3 py-3 border-b border-stone-800 ${c.active ? "" : "opacity-50"}`}
    style={mono}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-[14px] font-bold tracking-[0.12em] text-[#f5ecd6]">{c.code}</span>
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(c.code).then(() => toast.success(`Copied ${c.code}`))}
          data-testid={`invite-copy-${c.code}`}
          className="text-stone-500 hover:text-[#84cc16] transition-colors"
          title="Copy code"
        >
          <Copy size={13} />
        </button>
      </div>
      <div className="text-[11px] text-stone-500 truncate">
        {c.label || "—"} · {c.uses} visits · last {fmt(c.last_used_at)}
      </div>
    </div>
    <button
      type="button"
      onClick={() => onToggle(c)}
      data-testid={`invite-toggle-${c.code}`}
      className={`h-7 px-3 rounded-full text-[10px] font-bold uppercase tracking-[0.16em] border transition-colors ${
        c.active
          ? "border-[#84cc16] text-[#a3e635] hover:bg-[#84cc16] hover:text-[#0a0a0a]"
          : "border-stone-600 text-stone-400 hover:border-stone-400"
      }`}
    >
      {c.active ? "Active" : "Off"}
    </button>
  </li>
);

export const OwnerInvitesPanel = ({ open, onClose }) => {
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [codes, setCodes] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const call = useCallback(
    async (path = "", opts = {}) => {
      const r = await fetch(`${API}/codes${path}`, {
        ...opts,
        headers: { "Content-Type": "application/json", "X-Owner-Password": pw },
      });
      if (r.status === 401) throw new Error("Wrong owner password");
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    [pw]
  );

  const load = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      setCodes(await call());
      setAuthed(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const add = async (e) => {
    e.preventDefault();
    try {
      const c = await call("", { method: "POST", body: JSON.stringify({ label }) });
      setCodes((prev) => [...prev, c]);
      setLabel("");
      toast.success(`New code ${c.code}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggle = async (c) => {
    try {
      const u = await call(`/${c.code}`, { method: "PATCH", body: JSON.stringify({ active: !c.active }) });
      setCodes((prev) => prev.map((x) => (x.code === u.code ? u : x)));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const field =
    "flex-1 h-9 px-3 rounded-md bg-[#111] border border-stone-700 text-[12px] text-stone-100 placeholder:text-stone-600 focus:outline-none focus:border-[#84cc16]";

  return (
    <aside
      aria-hidden={!open}
      data-testid="owner-invites-panel"
      className={`fixed top-0 right-0 z-[60] h-full w-full sm:w-[460px] bg-[#0b0b0b]/95 backdrop-blur-xl border-l border-stone-700 shadow-[-24px_0_60px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-out ${
        open ? "translate-x-0" : "translate-x-full pointer-events-none"
      }`}
    >
      <header className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-stone-800">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#84cc16] mb-1" style={mono}>Owner only</div>
          <h2 className="text-3xl text-[#f5ecd6]" style={serif}>Invite codes</h2>
          <p className="text-[11px] text-stone-500 mt-1" style={mono}>
            One code per friend. Turning a code Off locks that person out on their next visit.
          </p>
        </div>
        <button type="button" onClick={onClose} data-testid="owner-invites-close" className="p-2 rounded-full text-stone-400 hover:text-white hover:bg-stone-800 transition-colors" aria-label="Close">
          <X size={18} />
        </button>
      </header>
      <div className="px-6 py-5 overflow-y-auto h-[calc(100%-120px)]">
        {!authed ? (
          <form onSubmit={load} className="flex gap-2" style={mono}>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Owner password" data-testid="owner-invites-password" className={field} />
            <button type="submit" disabled={!pw || busy} data-testid="owner-invites-unlock" className="h-9 px-4 rounded-md bg-[#84cc16] text-[#0a0a0a] text-[10px] font-bold uppercase tracking-[0.18em] disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Open"}
            </button>
          </form>
        ) : (
          <>
            <form onSubmit={add} className="flex gap-2 mb-4" style={mono}>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Friend's name (optional)" data-testid="owner-invites-label" className={field} />
              <button type="submit" data-testid="owner-invites-add" className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-[#84cc16] text-[#0a0a0a] text-[10px] font-bold uppercase tracking-[0.18em] hover:bg-[#a3e635] transition-colors">
                <Plus size={13} /> Add
              </button>
            </form>
            <ul data-testid="owner-invites-list">
              {codes.map((c) => (
                <CodeRow key={c.code} c={c} onToggle={toggle} />
              ))}
            </ul>
          </>
        )}
      </div>
    </aside>
  );
};
